/**
 * Four-tab catalog for VisionForge: HF · Cloudflare · CivitAI · Forge endpoints.
 */
import { useEffect, useRef } from "react";
import {
  fetchConnectionCreds,
  mountGpuTabs,
  PRIMARY_TOOL,
  type Connection,
  type ConnectionTab,
  type GpuMountHandle,
  type GpuPluginHost,
} from "@forgesuite/gpu-browser";
import "@forgesuite/gpu-browser/styles.css";
import { getAccessToken } from "../suite/auth";
import { loadSuiteSettings } from "../suite/settings";
import type { WalletSnapshot } from "../suite/types";
import {
  applyCatalogConnection,
  type InferenceSession,
} from "../lib/suiteInference";
import { useCatalogRuntime } from "../lib/useCatalogRuntime";

type Props = {
  wallet: WalletSnapshot | null;
  onStatus: (msg: string) => void;
  onSession: (s: InferenceSession | null) => void;
  session: InferenceSession | null;
  tab?: ConnectionTab;
  fill?: boolean;
  /** AccessHub tool_id — dump ∩ this tool's model_type. */
  tool?: string;
};

export function GpuBrowserMount({
  wallet,
  onStatus,
  onSession,
  session,
  tab = "cloudflare",
  fill = false,
  tool = PRIMARY_TOOL.visionforge,
}: Props) {
  const elRef = useRef<HTMLDivElement | null>(null);
  const handleRef = useRef<GpuMountHandle | null>(null);
  const busyRef = useRef(false);
  const { selectorOn } = useCatalogRuntime("visionforge");

  useEffect(() => {
    if (selectorOn !== true) return;
    const el = elRef.current;
    if (!el) return;

    const forgeApiUrl = loadSuiteSettings().forgeApiUrl;
    const isMember = Boolean(wallet?.subscriptionActive);
    const balanceUsd = wallet?.balanceUsd ?? 0;
    const memberPrice = 2.99;

    const host: GpuPluginHost = {
      productName: "VisionForge",
      appId: "visionforge",
      purpose: "vision",
      accessHubTool: tool || PRIMARY_TOOL.visionforge,
      dumpCatalogUrl: "https://proxy.vividlex.com",
      forgeApiUrl,
      getAccessToken,
      wallet: {
        balanceUsd,
        isMember,
        memberPriceUsd: memberPrice,
        subscriptionLabel: wallet?.subscriptionLabel ?? null,
      },
      adultOk: Boolean(wallet?.adultOk),
      catalogTabs: wallet?.catalogTabs || {},
      allowCivitaiRed: Boolean(wallet?.adultOk && wallet?.catalogTabs?.civitai_red !== false),
      instance: session?.instance
        ? ({
            id: session.instance.id,
            modelRepoId: session.instance.modelRepoId || session.modelId,
            modelName: session.instance.modelName || session.model.name,
            offerId: session.model.offer?.id || "",
            provider: session.instance.provider || session.source || "cloudflare",
            gpuName: session.instance.gpuName || "Suite",
            status: session.instance.status,
            endpointUrl: session.instance.endpointUrl,
            sessionToken: session.instance.sessionToken,
            pricePerHour: Number(session.model.offer?.pricePerHour) || 0,
            billingActive: Boolean(session.instance.billingActive),
          } as GpuPluginHost["instance"])
        : null,
      favoritesStorageKey: "visionforge.modelFavorites.v1",
      connectCtaLabel: "Connect",
      defaultInferenceModality: "text-generation",
      connectModalities: ["text-generation"],
      connecting: busyRef.current,
      onToast: (_l, message) => onStatus(message),
      onAccessHubModel: (modelId, meta) => {
        const now = new Date().toISOString();
        const s = applyCatalogConnection(
          {
            id: meta.dumpId || modelId,
            kind: "hf",
            sourceTable: "dump",
            sourceId: modelId,
            appOrigin: null,
            label: meta.name || modelId,
            modelId,
            engine: null,
            modality: "text-generation",
            endpointUrl: `${forgeApiUrl.replace(/\/$/, "")}/v1`,
            status: "ready",
            ready: true,
            visibility: "public",
            lastHealthAt: null,
            lastUsedAt: null,
            createdAt: now,
            updatedAt: now,
          },
          forgeApiUrl,
        );
        onSession(s);
        onStatus(`AccessHub pick: ${meta.name || modelId}`);
      },
      async onConnect(conn: Connection) {
        if (busyRef.current) return;
        busyRef.current = true;
        try {
          if (!getAccessToken()) {
            onStatus("Sign in with Google to connect a vision engine.");
            return;
          }
          let row = conn;
          if (!row.endpointUrl || row.kind === "forge_endpoint") {
            try {
              const creds = await fetchConnectionCreds({ forgeApiUrl, getAccessToken }, conn.id);
              row = {
                ...conn,
                endpointUrl: creds.endpointUrl || conn.endpointUrl,
                modelId: creds.modelId || conn.modelId,
              };
            } catch {
              /* catalog rows use the suite relay */
            }
          }
          const s = applyCatalogConnection(row, forgeApiUrl);
          onSession(s);
          onStatus(`Ready · ${s.model.name}`);
        } finally {
          busyRef.current = false;
        }
      },
    };

    handleRef.current?.destroy();
    handleRef.current = mountGpuTabs(el, {
      host,
      tab,
      defaultTab: "cloudflare",
      showTabBar: false,
    });

    return () => {
      handleRef.current?.destroy();
      handleRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectorOn,
    tab,
    wallet?.balanceUsd,
    wallet?.subscriptionActive,
    wallet?.adultOk,
    wallet?.catalogTabs?.civitai_red,
    wallet?.catalogTabs?.civitai,
    session?.instance?.id,
    session?.modelId,
    tool,
  ]);

  if (selectorOn !== true) return null;

  return (
    <div
      ref={elRef}
      className={`vf-gpu-browser-mount${fill ? " vf-gpu-browser-mount--fill" : ""}`}
    />
  );
}
